import { MODEL_MODES } from '../../LLM/index.js'

/**
 * Generate initial content for writing based on topic
 * @param {string} topic - The topic to write about
 * @param {object} llmService - The LLM service instance
 * @returns {Promise<{title: string, content: string}>} Generated title and content
 */
export const generateInitialContent = async (topic, llmService) => {
  const prompt = `Buatkan konten awal untuk dokumen dengan topik "${topic}". 

Buat:
1. Judul yang menarik dan relevan
2. Satu paragraf pembuka yang engaging (sekitar 100-150 kata) sebagai pemancing untuk memulai menulis

Format output:
TITLE: [judul dokumen]
CONTENT: [paragraf pembuka]

Pastikan paragraf pembuka:
- Menarik perhatian pembaca
- Memberikan gambaran umum tentang topik
- Mengundang untuk melanjutkan membaca
- Ditulis dalam bahasa Indonesia yang baik dan benar
- Tidak terlalu panjang, cukup sebagai pemancing awal`

  try {
    const response = await llmService.sendMessage(
      [{ role: 'user', content: prompt }],
      { 
        mode: MODEL_MODES.STANDARD,
        maxTokens: 500,
        stream: false,
        consumeCredits: false
      }
    )

    if (!response || response.trim().length === 0) {
      throw new Error('Respons kosong dari AI')
    }

    // Parse the response to extract title and content
    const lines = response.trim().split('\n')
    let title = ''
    let content = ''
    let isContent = false

    for (const line of lines) {
      if (line.startsWith('TITLE:')) {
        title = line.replace('TITLE:', '').trim()
      } else if (line.startsWith('CONTENT:')) {
        content = line.replace('CONTENT:', '').trim()
        isContent = true
      } else if (isContent && line.trim()) {
        content += ' ' + line.trim()
      }
    }

    // Fallback if parsing fails
    if (!title) {
      title = topic
    }
    if (!content) {
      content = response.trim()
    }

    console.log('✅ Initial content generated:', { title, contentLength: content.length })
    return { title, content }
  } catch (error) {
    console.error('❌ Error generating initial content:', error)
    throw new Error(`Gagal membuat konten awal: ${error.message}`)
  }
}

/**
 * Count words in text
 * @param {string} text - Text to count words from
 * @returns {number} Word count
 */
export const countWords = (text) => {
  if (!text || typeof text !== 'string') return 0
  return text.trim().split(/\s+/).filter(word => word.length > 0).length
}

/**
 * Count characters in text
 * @param {string} text - Text to count characters from
 * @returns {number} Character count
 */
export const countCharacters = (text) => {
  if (!text || typeof text !== 'string') return 0
  return text.length
}

/**
 * Calculate reading time estimate
 * @param {string} text - Text to calculate reading time for
 * @returns {number} Estimated reading time in minutes
 */
export const calculateReadingTime = (text) => {
  const wordsPerMinute = 200 // Average reading speed
  const wordCount = countWords(text)
  return Math.ceil(wordCount / wordsPerMinute)
}

/**
 * Format text for display
 * @param {string} text - Text to format
 * @returns {string} Formatted text
 */
export const formatText = (text) => {
  if (!text || typeof text !== 'string') return ''
  
  // Basic text formatting
  return text
    .replace(/\n\s*\n/g, '\n\n') // Normalize paragraph breaks
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim()
}

/**
 * Extract text from HTML or markdown
 * @param {string} content - Content to extract text from
 * @returns {string} Plain text
 */
export const extractPlainText = (content) => {
  if (!content || typeof content !== 'string') return ''
  
  // Remove HTML tags
  let text = content.replace(/<[^>]*>/g, '')
  
  // Remove markdown formatting
  text = text
    .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
    .replace(/\*(.*?)\*/g, '$1') // Italic
    .replace(/`(.*?)`/g, '$1') // Code
    .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Links
    .replace(/^#+\s*/gm, '') // Headers
    .replace(/^[-*+]\s*/gm, '') // Lists
    .replace(/^\d+\.\s*/gm, '') // Numbered lists
  
  return text.trim()
}

/**
 * Split text into pages based on A4 size estimation
 * @param {string} text - Text to split
 * @param {number} wordsPerPage - Estimated words per A4 page (default: 500)
 * @returns {string[]} Array of page contents
 */
export const splitIntoPages = (text, wordsPerPage = 500) => {
  if (!text || typeof text !== 'string') return ['']
  
  const words = text.trim().split(/\s+/)
  const pages = []
  
  for (let i = 0; i < words.length; i += wordsPerPage) {
    const pageWords = words.slice(i, i + wordsPerPage)
    pages.push(pageWords.join(' '))
  }
  
  return pages.length > 0 ? pages : ['']
}

/**
 * Generate document statistics
 * @param {string} content - Document content
 * @returns {object} Document statistics
 */
export const generateDocumentStats = (content) => {
  const plainText = extractPlainText(content)
  const wordCount = countWords(plainText)
  const charCount = countCharacters(plainText)
  const charCountNoSpaces = plainText.replace(/\s/g, '').length
  const readingTime = calculateReadingTime(plainText)
  const pageCount = Math.ceil(wordCount / 500) // Estimate 500 words per page
  
  return {
    words: wordCount,
    characters: charCount,
    charactersNoSpaces: charCountNoSpaces,
    readingTime,
    pages: pageCount
  }
}

/**
 * Validate document content
 * @param {string} content - Content to validate
 * @returns {object} Validation result
 */
export const validateContent = (content) => {
  const errors = []
  const warnings = []
  
  if (!content || content.trim().length === 0) {
    errors.push('Dokumen tidak boleh kosong')
  }
  
  const wordCount = countWords(content)
  if (wordCount < 10) {
    warnings.push('Dokumen terlalu pendek (kurang dari 10 kata)')
  }
  
  // Check for very long paragraphs
  const paragraphs = content.split('\n\n')
  const longParagraphs = paragraphs.filter(p => countWords(p) > 200)
  if (longParagraphs.length > 0) {
    warnings.push(`${longParagraphs.length} paragraf terlalu panjang (lebih dari 200 kata)`)
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  }
}

/**
 * Auto-save content to session storage
 * @param {string} key - Storage key
 * @param {object} data - Data to save
 */
export const autoSave = (key, data) => {
  try {
    const timestamp = new Date().toISOString()
    const saveData = {
      ...data,
      lastSaved: timestamp
    }
    sessionStorage.setItem(key, JSON.stringify(saveData))
    console.log('📁 Auto-saved document:', key)
  } catch (error) {
    console.warn('⚠️ Failed to auto-save:', error)
  }
}

/**
 * Load auto-saved content from session storage
 * @param {string} key - Storage key
 * @returns {object|null} Loaded data or null
 */
export const loadAutoSave = (key) => {
  try {
    const saved = sessionStorage.getItem(key)
    if (saved) {
      const data = JSON.parse(saved)
      console.log('📁 Loaded auto-saved document:', key, 'Last saved:', data.lastSaved)
      return data
    }
  } catch (error) {
    console.warn('⚠️ Failed to load auto-save:', error)
  }
  return null
}

/**
 * Export document as text file
 * @param {string} title - Document title
 * @param {string} content - Document content
 * @param {string} format - Export format ('txt' or 'md')
 */
export const exportDocument = (title, content, format = 'txt') => {
  try {
    const filename = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${format}`
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    console.log('📄 Document exported:', filename)
  } catch (error) {
    console.error('❌ Failed to export document:', error)
    throw new Error('Gagal mengekspor dokumen')
  }
}